import { SENSITIVE_CONTENT_LLM_MODEL_ID, promptQuoteModels } from '../llm/content-policy.js';

/** The fields the socket prices an LLM estimate on. */
export function estimateKey(params) {
  return JSON.stringify({ tokenType: params.tokenType, model: params.model, messages: params.messages, max_tokens: params.max_tokens });
}

/**
 * Whether a token-billed quote must consider the reroute model at all. The
 * socket reroutes only a flagged request (`safeContentFilter: false`) from an
 * account holding Premium Spark. Whether the reroute model is online right now
 * is learned from its live estimate (probeRerouteModel), never from the SDK's
 * cached model list, which the socket refreshes only on image-worker
 * broadcasts and reconnects.
 */
export function rerouteCeilingCandidate({ requests, premiumSpark }) {
  return Number(premiumSpark) > 0 && requests.some((params) => params.safeContentFilter === false);
}

/** The estimate endpoint answers 404 while a model has no workers: the socket's own gate for rerouting. */
function isModelUnavailable(error) {
  return error?.status === 404;
}

/**
 * Whether the socket's reroute model has workers right now, learned from its
 * live estimate for the first flagged request shape. An answer means workers;
 * a 404 means none, so the socket cannot reroute; any other failure is thrown
 * so the caller fails closed. A list with no flagged request cannot be
 * rerouted and is never probed.
 */
export async function probeRerouteModel(estimate, requests) {
  const flagged = requests.find((params) => params.safeContentFilter === false);
  if (!flagged) return false;
  try {
    await estimate({ ...flagged, model: SENSITIVE_CONTENT_LLM_MODEL_ID });
    return true;
  } catch (error) {
    if (isModelUnavailable(error)) return false;
    throw error;
  }
}

/**
 * Stop before a rung when a reroute became possible after the reviewed quote
 * left it out (the model came online, or Premium Spark arrived). A ceiling
 * reviewed above what the socket can now charge is safe and passes.
 */
export function assertRerouteCeilingCurrent({ applied, appliesNow, request, quote }) {
  if (applied || !appliesNow) return;
  const error = new Error('The price changed. Review this prompt tool again.');
  error.code = 'QUOTE_CHANGED';
  error.request = request;
  if (quote) error.quote = quote;
  throw error;
}

/**
 * Total Spark units for a prompt-tool request list, and whether the reroute
 * ceiling was applied.
 *
 * `estimate(params)` resolves the SDK cost of one request shape; identical
 * shapes are estimated once per pass. With `ceiling`, the reroute model is
 * probed once (its estimate for the first flagged shape doubles as the price
 * of that shape); while it answers, every flagged request is priced at the
 * higher of the named and reroute estimates, so a reviewed amount is never
 * below what the socket can charge. A 404 means it has no workers and the
 * named price stands for this pass; any other failure of either estimate
 * fails the quote. The caller decides afresh on every pass (the quote and its
 * post-review requote), so a change in between raises QUOTE_CHANGED there.
 */
export async function quotePromptRequestUnits(requests, { estimate, toUnits, ceiling = false }) {
  const estimates = new Map();
  const cached = (params) => {
    const key = estimateKey(params);
    if (!estimates.has(key)) estimates.set(key, estimate(params));
    return estimates.get(key);
  };
  const applied = ceiling && await probeRerouteModel(cached, requests);
  let total = 0n;
  for (const params of requests) {
    let units = toUnits((await cached(params)).costInSpark);
    for (const modelId of promptQuoteModels(params, { ceiling: applied })) {
      if (modelId === params.model) continue;
      const candidate = toUnits((await cached({ ...params, model: modelId })).costInSpark);
      if (candidate > units) units = candidate;
    }
    total += units;
  }
  return { total, applied };
}
